import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, TrendingUp, Loader2, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditBoostPricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const packages = [
    {
      id: 'starter',
      name: 'Quick Fix',
      price: 49,
      period: 'one-time',
      description: 'Perfect for quick wins',
      features: [
        'Personalized 30-day action plan',
        'Score simulator',
        'Priority actions identified',
        'PDF download',
      ],
      cta: 'Get Quick Fix',
      popular: false,
    },
    {
      id: 'complete',
      name: 'Complete Repair',
      price: 99,
      period: 'one-time',
      description: 'Full credit transformation',
      features: [
        'Everything in Quick Fix',
        'Comprehensive 90-day plan',
        'Dispute letter templates',
        'Creditor negotiation scripts',
        'Goodwill letter templates',
        'Weekly milestones',
      ],
      cta: 'Get Complete Plan',
      popular: true,
    },
    {
      id: 'subscription',
      name: 'Monthly Coaching',
      price: 19,
      period: '/month',
      description: 'Ongoing support',
      features: [
        'Unlimited action plans',
        'Unlimited dispute letters',
        'Score tracking',
        'Monthly check-ins',
        'Priority support',
      ],
      cta: 'Start Coaching',
      popular: false,
    },
  ];

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    setLoading(packageId);

    try {
      const token = localStorage.getItem('careerboost_token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${BACKEND_URL}/api/creditboost/checkout`,
        { package_id: packageId, origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/creditboost" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CreditBoost
              </span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/creditboost/dashboard">
                  <Button variant="outline">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost">Log in</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-blue-600 hover:bg-blue-700">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Invest in Your Credit Future
          </h1>
          <p className="text-lg text-slate-600">
            Choose the plan that fits your credit repair needs
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`relative ${pkg.popular ? 'border-blue-500 border-2 shadow-xl' : 'border-slate-200'}`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-blue-600 text-white text-sm font-medium">
                    <Crown className="w-4 h-4" />
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pkg.name}
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                  <span className="text-slate-500 ml-1">{pkg.period}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={loading === pkg.id}
                  className={`w-full ${
                    pkg.popular
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                  data-testid={`buy-btn-${pkg.id}`}
                >
                  {loading === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {pkg.cta}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust */}
        <div className="text-center mt-12 pt-8 border-t border-slate-200">
          <p className="text-slate-500 text-sm">
            Secure payment via Stripe • Instant delivery • 30-day money-back guarantee
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            Founded by <span className="text-white font-medium">Robert Elder III</span>, CEO
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CreditBoostPricing;
